import { IS_CLIENT, IS_PRODUCTION, sha256Hash } from '@repo/common-utils';
import mixpanel, { Config } from 'mixpanel-browser';

import { MixpanelInitializeProps, SendEventParams, SetUserParams, EventTrackingInitializeProps } from '../types';

import { BaseService } from './BaseService';

export class MixpanelService extends BaseService {
  private readonly mixpanelKey?: string;
  private readonly mixpanelConfig?: Partial<Config>;
  private readonly landingId?: string;
  private isInit: boolean = false;

  constructor({ appId, clientId, mixpanelKey, mixpanelConfig, landingId }: EventTrackingInitializeProps & MixpanelInitializeProps) {
    super(appId, clientId);
    this.mixpanelKey = mixpanelKey;
    this.landingId = landingId;
    this.mixpanelConfig = { debug: !IS_PRODUCTION, track_pageview: false, persistence: 'cookie', cross_subdomain_cookie: true, ...mixpanelConfig };
    this.init();
  }

  protected init() {
    if (!IS_CLIENT || !this.mixpanelKey) return;

    mixpanel.init(this.mixpanelKey, this.mixpanelConfig);
    const attributes = {
      ...this.commonAttributes,
      ...(this.landingId ? { landing_id: this.landingId } : { landing_id: null })
    };
    mixpanel.register(attributes);
    this.isInit = true;
  }

  get mixpanelDeviceId(): string {
    if (this.isInit) {
      return mixpanel.get_property('$device_id');
    }
    return '';
  }

  async setUser({ id = '', name = '', phone = '', email = '' }: SetUserParams) {
    try {
      if (this.isInit) {
        mixpanel.identify(id);
        mixpanel.people.union({
          user_id: id,
          name_hash: await sha256Hash(name || ''),
          phone_hash: await sha256Hash(phone || ''),
          email_hash: await sha256Hash(email || '')
        });
      }
    } catch (err) {
      console.info('mixpanel set user error', err);
      throw err;
    }
  }

  sendEvent(params: SendEventParams) {
    if (this.isInit) {
      const eventNameParams = [params?.category, params?.object, params?.detail, params?.action];
      const name = eventNameParams.filter((value) => !!value).join('_');
      const attributes = {
        ...this.commonAttributes,
        ...(this.landingId && { landing_id: this.landingId }),
        ...params?.attributes,
        is_server: false
      };
      mixpanel.track(name, attributes, {
        send_immediately: true
      });

      return {
        config: {
          distinct_id: mixpanel.get_distinct_id(),
          ...this.mixpanelConfig
        },
        event: {
          name: name,
          ...params?.attributes,
          ...this.commonAttributes,
          ...(this.landingId && { landing_id: this.landingId }),
          is_server: false
        }
      };
    }
  }

  unsetUser(): void {
    mixpanel.reset();
  }
}
